import { type ElementType, type ReactNode } from 'react';

import { COLOR_TOKENS, type ColorToken } from '@backrs_ui/common-ui/src/designSystem/colors/colors';
import { AppIcon, type AppIcons } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { getSelectMenuPaperStyles } from '@backrs_ui/common-ui/src/designSystem/select/select.styles';
import { visuallyHiddenSx } from '@backrs_ui/common-ui/src/utils/a11y.util';

import {
  Box,
  MenuItem,
  Select,
  type SelectChangeEvent,
  type SxProps,
  type Theme,
  Typography,
} from '@mui/material';

const CHEVRON_DIAMETER = '20px';
const MENU_MAX_HEIGHT = 360;

export type FilterSelectOption = Readonly<{ value: string; label: string }>;

type FilterSelectShellProps = Readonly<{
  id: string;
  /** Rendered in the closed control. Owns its own layout, including any adornment. */
  renderValue: () => ReactNode;
  children: ReactNode;
  displayEmpty?: boolean;
  /** Replaces the dropdown indicator. The theme hides it by default; supplying this re-enables it. */
  IconComponent?: ElementType;
  sx?: SxProps<Theme>;
  /** Element carrying the visible label, so the combobox is named by it rather than by itself. */
  labelId?: string;
}> &
  (
    | Readonly<{
        multiple: true;
        value: string[];
        onChange: (value: string[]) => void;
      }>
    | Readonly<{
        multiple?: false;
        value: string;
        onChange: (value: string) => void;
      }>
  );

/**
 * The shared filter-control chrome: menu placement and paper styling, and the `id` wiring
 * that puts the test id on the combobox rather than the hidden native input.
 * Callers supply the closed-state content and the menu items.
 */
export const FilterSelect = (props: FilterSelectShellProps) => {
  const { id, renderValue, children, displayEmpty, IconComponent, sx, labelId } = props;

  const shared = {
    SelectDisplayProps: { id },
    // Without this MUI joins only the display element's own id, so the control's accessible
    // name is computed from its current value and the label is never announced.
    ...(labelId ? { labelId } : {}),
    size: 'small' as const,
    displayEmpty,
    MenuProps: FILTER_MENU_PROPS,
    renderValue,
    ...(IconComponent ? { IconComponent } : {}),
    sx: [
      ...(IconComponent ? [showIconSx] : []),
      ...(Array.isArray(sx) ? sx : [sx]),
    ] as SxProps<Theme>,
  };

  if (props.multiple) {
    return (
      <Select
        {...shared}
        multiple
        value={props.value}
        onChange={(event: SelectChangeEvent<string[]>) => {
          const next = event.target.value;

          props.onChange(typeof next === 'string' ? next.split(',') : next);
        }}
      >
        {children}
      </Select>
    );
  }

  return (
    <Select
      {...shared}
      value={props.value}
      onChange={(event: SelectChangeEvent<string>) => props.onChange(event.target.value)}
    >
      {children}
    </Select>
  );
};

type LabeledFilterSelectProps = Readonly<{
  id: string;
  label: string;
  value: string;
  options: readonly FilterSelectOption[];
  onChange: (value: string) => void;
  /** Small status dot shown before the selected label. */
  dotColor?: ColorToken;
  /** Shown for the empty value, which then also heads the menu as the un-narrowed choice. */
  placeholder?: string;
  /** Leads the selected label inside the control; the text label above is then hidden but still names the combobox. */
  iconName?: AppIcons;
  sx?: SxProps<Theme>;
}>;

/** Single-select filter with its label above the control and plain menu items. */
export const LabeledFilterSelect = ({
  id,
  label,
  value,
  options,
  onChange,
  dotColor,
  placeholder,
  iconName,
  sx,
}: LabeledFilterSelectProps) => {
  const isEmpty = value === '';
  const selectedLabel =
    isEmpty && placeholder !== undefined
      ? placeholder
      : (options.find((option) => option.value === value)?.label ?? value);

  return (
    <Box sx={fieldSx}>
      <Typography
        id={`${id}-label`}
        variant="text-sm-medium"
        sx={iconName ? visuallyHiddenSx : fieldLabelSx}
      >
        {label}
      </Typography>
      <FilterSelect
        id={id}
        labelId={`${id}-label`}
        value={value}
        onChange={onChange}
        displayEmpty={placeholder !== undefined}
        IconComponent={ChevronIcon}
        sx={[selectSx, ...(Array.isArray(sx) ? sx : [sx])]}
        renderValue={() => (
          <Box sx={valueRowSx}>
            {iconName ? (
              <AppIcon
                iconName={iconName}
                diameter={CHEVRON_DIAMETER}
                color="Colors/Foreground/fg-quaternary"
              />
            ) : null}
            {dotColor ? (
              <Box sx={dotSx} style={{ backgroundColor: COLOR_TOKENS[dotColor] }} />
            ) : null}
            <Typography
              variant="text-md-medium"
              sx={isEmpty && placeholder !== undefined ? placeholderLabelSx : valueLabelSx}
            >
              {selectedLabel}
            </Typography>
          </Box>
        )}
      >
        {placeholder !== undefined ? (
          <MenuItem id={`${id}-option-empty`} value="">
            {placeholder}
          </MenuItem>
        ) : null}
        {options.map((option) => (
          <MenuItem id={`${id}-option-${option.value}`} key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </FilterSelect>
    </Box>
  );
};

type LabeledFilterMultiSelectProps = Readonly<{
  id: string;
  label: string;
  values: readonly string[];
  options: readonly FilterSelectOption[];
  onChange: (values: string[]) => void;
  /** Shown while nothing is selected, where a single-select would show its value. */
  placeholder: string;
  /** Shown in place of the names once more than one option is selected. */
  selectedCountLabel: (count: number) => string;
  sx?: SxProps<Theme>;
}>;

/**
 * Multi-select counterpart of LabeledFilterSelect: same label-above-control chrome, with
 * checkbox menu items and a closed state that names a single choice and counts several.
 */
export const LabeledFilterMultiSelect = ({
  id,
  label,
  values,
  options,
  onChange,
  placeholder,
  selectedCountLabel,
  sx,
}: LabeledFilterMultiSelectProps) => {
  const selectedLabels = options
    .filter((option) => values.includes(option.value))
    .map((option) => option.label);
  const hasSelection = selectedLabels.length > 0;
  const displayLabel = !hasSelection
    ? placeholder
    : selectedLabels.length === 1
      ? selectedLabels[0]
      : selectedCountLabel(selectedLabels.length);

  return (
    <Box sx={fieldSx}>
      <Typography id={`${id}-label`} variant="text-sm-medium" sx={fieldLabelSx}>
        {label}
      </Typography>
      <FilterSelect
        multiple
        id={id}
        labelId={`${id}-label`}
        displayEmpty
        value={[...values]}
        onChange={onChange}
        IconComponent={ChevronIcon}
        sx={[selectSx, ...(Array.isArray(sx) ? sx : [sx])]}
        renderValue={() => (
          <Box sx={valueRowSx}>
            <Typography
              variant="text-md-medium"
              sx={hasSelection ? valueLabelSx : placeholderLabelSx}
            >
              {displayLabel}
            </Typography>
          </Box>
        )}
      >
        {options.map((option) => {
          const isSelected = values.includes(option.value);

          return (
            <MenuItem
              id={`${id}-option-${option.value}`}
              key={option.value}
              value={option.value}
              disableRipple
            >
              <Box sx={checkboxRowSx}>
                <Box id={`${id}-checkbox-${option.value}`} sx={checkboxSx(isSelected)}>
                  <AppIcon
                    iconName={isSelected ? 'Check' : 'Square'}
                    diameter="16px"
                    color={isSelected ? 'Colors/Foreground/fg-white' : 'currentColor'}
                  />
                </Box>
                <Typography variant="text-md-medium">{option.label}</Typography>
              </Box>
            </MenuItem>
          );
        })}
      </FilterSelect>
    </Box>
  );
};

type ChevronIconProps = Readonly<{ className?: string }>;

const ChevronIcon = ({ className }: ChevronIconProps) => (
  <Box className={className} sx={chevronSx}>
    <AppIcon
      iconName="ChevronDown"
      diameter={CHEVRON_DIAMETER}
      color="Colors/Foreground/fg-quaternary"
    />
  </Box>
);

// the theme hides the indicator globally, so a supplied IconComponent needs it back
const showIconSx: SxProps<Theme> = {
  '& .MuiSelect-icon': {
    display: 'block',
  },
};

const filterMenuPaperSx: SxProps<Theme> = (theme) => ({
  ...getSelectMenuPaperStyles(theme),
  maxHeight: MENU_MAX_HEIGHT,
});

const FILTER_MENU_PROPS = {
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  PaperProps: { sx: filterMenuPaperSx },
} as const;

const chevronSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: `0 ${backrs.spacing['spacing-md']}`,
});

const fieldSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-md'],
  minWidth: 0,
  width: '100%',
});

const fieldLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const selectSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  borderRadius: backrs.radius['radius-md'],
  boxShadow: backrs.shadows['shadow-xs'],
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: backrs.colors['Colors/Border/border-primary'],
  },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    padding: `${backrs.spacing['spacing-md']} ${backrs.spacing['spacing-lg']} !important`,
    // clears the absolutely-positioned chevron: its 20px icon plus spacing-md on each side
    paddingRight: `calc(${CHEVRON_DIAMETER} + ${backrs.spacing['spacing-md']} * 2) !important`,
  },
});

const valueRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
  minWidth: 0,
});

const valueLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const placeholderLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-placeholder'],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const checkboxRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
});

const checkboxSx =
  (isChecked: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    flexShrink: 0,
    borderRadius: backrs.radius['radius-xs'],
    backgroundColor: isChecked ? backrs.colors['Colors/Background/bg-brand-solid'] : 'transparent',
    border: isChecked ? `1px solid ${backrs.colors['Colors/Base/Black']}` : 'none',
  });

const dotSx: SxProps<Theme> = ({ backrs }) => ({
  width: 8,
  height: 8,
  flexShrink: 0,
  borderRadius: backrs.radius['radius-full'],
});
